import React from "react";
import { Button } from "reactstrap";
import "./photo-picker.css";

export const MAX_SIZE_MB = 5;
export const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

function bytesToMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

export default function PhotoPicker({
  label = "Photo",
  file,
  preview,
  onPick,
  onClear,
  notify, // (msg, type) => void  // Use NotificationComponent
  fieldName = "photo", //  connect with multer upload.single("photo")
}) {
  const inputRef = React.useRef(null);
  const dropRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const validateFile = (f) => {
    if (!f) return false;
    if (!ACCEPTED_TYPES.includes(f.type)) {
      notify?.("Only JPG, PNG or WebP are allowed.", "warning");
      return false;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      notify?.(
        `Image is too large (${bytesToMB(
          f.size
        )} MB). Max allowed is ${MAX_SIZE_MB} MB.`,
        "warning"
      );
      return false;
    }
    return true;
  };

  const handleFiles = (files) => {
    const f = files?.[0];
    if (validateFile(f)) {
      const url = URL.createObjectURL(f);
      onPick?.(f, url, fieldName);
    }
  };

  const onInputChange = (e) => handleFiles(e.target.files);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove("pp--dragover");
    handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add("pp--dragover");
  };
  const onDragLeave = () => {
    dropRef.current?.classList.remove("pp--dragover");
  };

  // Paste (Ctrl/Cmd + V)
  React.useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (validateFile(f)) {
            const url = URL.createObjectURL(f);
            onPick?.(f, url, fieldName);
            break;
          }
        }
      }
    };
    const el = dropRef.current;
    el?.addEventListener("paste", onPaste);
    return () => el?.removeEventListener("paste", onPaste);
  }, [onPick, fieldName]);

  return (
    <div className="photo-picker">
      <label className="form-label mb-2">{label}</label>

      <div
        ref={dropRef}
        className="pp--dropzone"
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        tabIndex={0}
        role="button"
        aria-label="Upload image by clicking, dragging or pasting"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        {!preview ? (
          <div className="pp--placeholder">
            <div className="pp--icon" aria-hidden>
              🖼️
            </div>
            <div className="pp--text">
              <strong>Drop an image</strong> or{" "}
              <span
                className="pp--link"
                onClick={() => inputRef.current?.click()}
              >
                click to upload
              </span>
              <div className="pp--hint">You can also paste (Ctrl/Cmd + V)</div>
              <div className="pp--subhint">
                Max {MAX_SIZE_MB} MB • JPG/PNG/WebP
              </div>
            </div>
          </div>
        ) : (
          <div className="pp--preview">
            <div className="pp--imageWrap">
              <img src={preview} alt="preview" className="pp--image" />
            </div>
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="pp--filename text-truncate">
                {file?.name ? file.name : "Selected image"}
                {file?.size ? ` • ${bytesToMB(file.size)} MB` : null}
              </div>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  onClick={() => inputRef.current?.click()}
                >
                  Change
                </Button>
                <Button size="sm" color="danger" onClick={onClear}>
                  Remove
                </Button>
              </div>
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          onChange={onInputChange}
          className="d-none"
        />
      </div>
    </div>
  );
}
